import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DraggableAttributes,
} from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classnames from "classnames";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import { FormattedPlural, useIntl } from "react-intl";

import { getTranslation } from "../../../../../utils/translation";
import ServiceComponent from "../service";
import IntlMessages from "../../../../../utils/messages";
import images from "../../../../../images";
import ModalComponent from "../../../../../components/Tailwind/Modal";
import { getCypressTestId } from "../../../../../utils/config";
import {
  canDeleteService,
  canEditCategory,
  canManageCategories,
} from "../../../../../utils/types";
import { EnumServiceType } from "../../../../../lib/ground-aws-graphql-core/api/graphql/types";
import { Category } from "../../../../../lib/ground-aws-graphql-core/models/Category";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import { Service } from "../../../../../lib/ground-aws-graphql-core/models/Service";
import { Product } from "../../../../../lib/ground-aws-graphql-core/models/Product";
import Dropdown from "../../../../../components/Tailwind/Dropdown";
import { GroundAuthContextStore } from "../../../../../lib/ground-aws-cognito-auth-core";

type SortableServiceProps = {
  service: Service;
  match: Match<{ cid: string }>;
  serviceType: EnumServiceType;
  onEditService: (e: any, service: Service) => void;
  onClickService: (e: any, service: Service) => void;
  fetchData: () => void;
};

type DragHandleProps = {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
};

const DragHandle = ({ attributes, listeners }: DragHandleProps) => (
  <button
    type="button"
    aria-label="Déplacer service"
    {...attributes}
    {...listeners}
    style={{ touchAction: "none" }}
    className="cursor-grab active:cursor-grabbing pr-4"
  >
    <img src={images.drag} alt="⋮⋮" />
  </button>
);

function SortableServiceComponent({
  service,
  match,
  serviceType,
  onEditService,
  onClickService,
  fetchData,
}: SortableServiceProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center bg-gray-100"
    >
      <div className="flex-1">
        <ServiceComponent
          match={match}
          key={service.id}
          service={service}
          serviceType={serviceType}
          onEditService={onEditService}
          onClickService={onClickService}
          fetchData={fetchData}
        />
      </div>
      <DragHandle attributes={attributes} listeners={listeners} />
    </div>
  );
}
const SortableService = React.memo(SortableServiceComponent);

type Props = {
  match: Match<{ cid: string }>;
  category: Category;
  services: Service[];
  serviceType: EnumServiceType;
  onEditService: (e: any, service: Service) => void;
  onClickService: (e: any, service: Service) => void;
  onOrderService: (
    id: string,
    categoryId: string,
    buildingId: string,
    beforeId?: string,
    afterId?: string
  ) => void;
  onAddService: (category: Category) => void;
  onEditCategory: (category: Category) => void;
  onDuplicateCategory: (category: Category) => void;
  fetchData: () => void;
};

function CategoryComponent({
  match,
  category,
  services,
  serviceType,
  onEditService,
  onClickService,
  onOrderService,
  onEditCategory,
  onDuplicateCategory,
  onAddService,
  fetchData,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localServices, setLocalServices] = useState<Service[]>(services);
  const intl = useIntl();

  useEffect(() => setLocalServices(services), [services]);

  const deleteCategory = GroundGraphqlContextStore.useStoreActions(
    actions => actions.category.deleteCategory
  );

  const me = GroundAuthContextStore.useStoreState(
    state => state.authentication.me
  );

  const handleIsOpenToggler = () => setIsOpen(prev => !prev);

  const saveServiceOrder = useCallback(async (id, ids: string[]) => {
    console.log("Nouvel ordre des services :", ids);
    const newIndex = ids.findIndex(cId => cId === id);

    const beforeId = newIndex > 0 ? ids[newIndex - 1] : undefined;
    const afterId = newIndex < ids.length - 1 ? ids[newIndex + 1] : undefined;
    onOrderService(id, category.id, match.params.cid, beforeId, afterId);
  }, []);

  const handleModalValidate = () => {
    deleteCategory({ id: category.id, markForDelete: true })
      .then(() => {
        const message = intl.formatMessage({
          id: "page.list.categories.delete.category.success",
        });
        toast(message, { type: "success" });
      })
      .catch(() => {
        const message = intl.formatMessage({
          id: "page.list.categories.delete.category.error",
        });
        toast(message, { type: "error" });
        setIsModalOpen(false);
      });
  };

  const categoryName = getTranslation(category.name);

  const modalText = (
    <IntlMessages
      id="general.delete.category"
      values={{ name: categoryName }}
    />
  );

  const classNameEnabled = (enabled: boolean) =>
    classnames("block h-2 w-2 rounded-full text-white shadow-solid", {
      "bg-red-400": !enabled,
      "bg-green-400": enabled,
    });

  const values = [
    {
      id: "add_service",
      label: "page.list.services.create.service",
      icon: images.addIcon,
      onClick: () => onAddService(category),
    },
  ];

  if (canManageCategories(me)) {
    values.push({
      id: "duplicate_category",
      label: "general.duplicate",
      icon: images.duplicate,
      onClick: () => onDuplicateCategory(category),
    });
  }

  if (canEditCategory(me, category?.center)) {
    values.unshift({
      id: "edit_category",
      label: "general.edit",
      icon: images.edit,
      onClick: () => onEditCategory(category),
    });
  }

  if (canDeleteService(me)) {
    values.push({
      id: "delete_service",
      label: "general.delete",
      icon: images.deleteIcon,
      onClick: () => setIsModalOpen(true),
    });
  }

  const products = services.reduce((acc: Product[], el: Service) => {
    const p = el.products?.items?.filter(i => !i.provider?.markForDelete);
    if (p && p.length > 0) acc.push(...p);

    return acc;
  }, []);

  return (
    <div className="shadow-ground-2 border border-ground-gray-200 border-solid mb-8">
      <div className="flex flex-row items-center py-4 px-8">
        <div className="pl-4 flex-1">
          <div className="flex items-center">
            <span className={classNameEnabled(category.enabled)} />
            <button
              data-cy={`category-name-${category.id}`}
              data-testid={getCypressTestId(category)}
              className={`block text-gray-900 text-16px font-semibold ml-2 ${
                canEditCategory(me, category?.center)
                  ? "cursor-pointer"
                  : "cursor-default"
              }`}
              onClick={
                canEditCategory(me, category?.center)
                  ? () => onEditCategory(category)
                  : undefined
              }
              type="button"
            >
              <span className={category?.center ? "italic" : ""}>
                {categoryName}
              </span>
            </button>
          </div>
          <button
            data-cy={`total-services-toggle-${category.id}`}
            data-testid={getCypressTestId(category)}
            className="block text-gray-600 text-13px cursor-pointer"
            onClick={handleIsOpenToggler}
            type="button"
          >
            {services.length}{" "}
            <FormattedPlural
              value={services.length}
              one={<IntlMessages id="page.list.services.service.one" />}
              other={<IntlMessages id="page.list.services.service.many" />}
            />{" "}
            - {products.length}{" "}
            <FormattedPlural
              value={products.length}
              one={
                <IntlMessages
                  id={`page.list.services.product.${serviceType}.one`}
                />
              }
              other={
                <IntlMessages
                  id={`page.list.services.product.${serviceType}.many`}
                />
              }
            />
          </button>
        </div>
        {category?.parent?.name && (
          <div className="pl-4 flex-1 align-middle items-end text-ground-gray-100">
            <span>{getTranslation(category.parent.name)}</span>
          </div>
        )}
        <div>
          <button
            data-cy={`category-toggle-services-${category.id}`}
            data-testid={getCypressTestId(category)}
            className="flex flex-row items-center text-12px text-ground-blue-100 cursor-pointer"
            onClick={handleIsOpenToggler}
            type="button"
          >
            <img
              src={isOpen ? images.closeBlue : images.addBlue}
              alt={isOpen ? "-" : "+"}
            />
            <IntlMessages id={isOpen ? "general.close" : "general.open"} />
          </button>
        </div>

        <div className="pl-4">
          <Dropdown
            values={values}
            dataCy={`category-dropdown-${category.id}`}
          />
        </div>
      </div>

      <div
        className={isOpen ? "block divide-y divide-ground-gray-200" : "hidden"}
      >
        <DndContext
          sensors={useSensors(
            useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
          )}
          collisionDetection={closestCenter}
          onDragEnd={event => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            setLocalServices(prev => {
              const oldIndex = prev.findIndex(s => s.id === active.id);
              const newIndex = prev.findIndex(s => s.id === over.id);
              const newOrder = arrayMove(prev, oldIndex, newIndex);

              saveServiceOrder(
                active.id,
                newOrder.map(s => s.id)
              );

              return newOrder;
            });
          }}
        >
          <SortableContext
            items={localServices.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {localServices.map(service => (
              <SortableService
                key={service.id}
                service={service}
                match={match}
                serviceType={serviceType}
                onEditService={onEditService}
                onClickService={onClickService}
                fetchData={fetchData}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        text={modalText}
        buttonValidate={<IntlMessages id="general.delete" />}
        buttonCancel={<IntlMessages id="general.cancel" />}
        onClickValidate={handleModalValidate}
        onClickCancel={() => setIsModalOpen(false)}
      />
    </div>
  );
}

export default React.memo(CategoryComponent);
